import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogOut, ShoppingCart, Package, Users, TrendingUp, FileText, Printer, Calendar, UserPlus, Trash2, Key, Edit, DollarSign, Shield, Truck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { userAPI, vendorAPI, invoiceAPI, sessionAPI, statsAPI, cashoutAPI } from '../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const username = localStorage.getItem('username') || 'User';
  const userRole = localStorage.getItem('userRole') || 'user';
  const userName = localStorage.getItem('userName') || 'User';
  
  const [sessionOpeningCash, setSessionOpeningCash] = useState(parseFloat(localStorage.getItem('openingCash')) || 0);
  const [sessionCashSales, setSessionCashSales] = useState(0);
  const [sessionCashout, setSessionCashout] = useState(0);
  const [expectedCash, setExpectedCash] = useState(0);
  const openingCash = sessionOpeningCash.toFixed(2);
  
  const [todayInvoices, setTodayInvoices] = useState([]);
  const [yesterdayInvoices, setYesterdayInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [salesStats, setSalesStats] = useState({ total_sales: 0, upi_sales: 0, card_sales: 0, cash_sales: 0, total_cashout: 0, invoice_count: 0 });
  const [loading, setLoading] = useState(true);
  const [activeInvoiceTab, setActiveInvoiceTab] = useState('today');
  const [salesView, setSalesView] = useState('today');
  
  // Closing Cash Dialog
  const [showClosingCashDialog, setShowClosingCashDialog] = useState(false);
  const [closingCash, setClosingCash] = useState('');
  
  // Shift Handover Dialog
  const [showHandoverDialog, setShowHandoverDialog] = useState(false);
  const [handoverData, setHandoverData] = useState(null);
  
  // User Management Dialog
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [users, setUsers] = useState([]);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', password: '', name: '', role: 'user' });
  
  // Change Password Dialog
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  
  // Edit Profile Dialog (Admin only)
  const [showEditProfileDialog, setShowEditProfileDialog] = useState(false);
  const [profileData, setProfileData] = useState({ name: '' });
  
  // Vendor Management
  const [showAddVendorDialog, setShowAddVendorDialog] = useState(false);
  const [showVendorList, setShowVendorList] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [newVendor, setNewVendor] = useState({ name: '', contact: '', address: '', gst: '', email: '' });

  const getFormattedDate = (daysAgo = 0) => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).split('/').join('/');
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const today = getFormattedDate(0);
      const yesterday = getFormattedDate(1);
      
      // Load today's invoices, yesterday's invoices, sales stats, and active session
      const [todayInvoicesData, yesterdayInvoicesData, stats, currentSession] = await Promise.all([
        invoiceAPI.getAll(today),
        invoiceAPI.getAll(yesterday),
        statsAPI.getSales(today),
        sessionAPI.getCurrent().catch(() => null),
      ]);
      
      setTodayInvoices(todayInvoicesData);
      setYesterdayInvoices(yesterdayInvoicesData);
      setSalesStats(stats);
      
      // Use opening cash and live calculations from active session in DB (source of truth)
      if (currentSession && currentSession.opening_cash !== undefined) {
        setSessionOpeningCash(currentSession.opening_cash);
        setSessionCashSales(currentSession.total_cash_sales || 0);
        setSessionCashout(currentSession.total_cashout || 0);
        setExpectedCash(currentSession.expected_cash || currentSession.opening_cash);
        localStorage.setItem('openingCash', currentSession.opening_cash.toFixed(2));
      }
      
      // Load users if admin
      if (userRole === 'admin') {
        const usersData = await userAPI.getAll();
        setUsers(usersData);
      }
      const vendorsData = await vendorAPI.getAll();
      setVendors(vendorsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      if (error.message.includes('Session expired')) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [userRole, navigate]);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    
    loadData();
  }, [navigate, loadData]);

  // Auto-trigger closing cash dialog when redirected from other pages
  useEffect(() => {
    if (searchParams.get('action') === 'logout') {
      if (userRole === 'admin') {
        localStorage.clear();
        navigate('/');
      } else {
        setShowClosingCashDialog(true);
      }
    }
  }, [searchParams, userRole, navigate]);

  const handleLogoutClick = () => {
    if (userRole === 'admin') {
      localStorage.clear();
      toast({ title: "Logged Out", description: "Admin session ended" });
      navigate('/');
    } else {
      setShowClosingCashDialog(true);
    }
  };

  const handleCancelLogout = () => {
    setShowClosingCashDialog(false);
    setClosingCash('');
    // Clear the action=logout param if present
    if (searchParams.get('action') === 'logout') {
      navigate('/dashboard', { replace: true });
    }
  };

  const handleClosingCashSubmit = async () => {
    const cashValue = parseFloat(closingCash);
    
    if (isNaN(cashValue) || closingCash.trim() === '') {
      toast({
        title: "Error",
        description: "Please enter a valid closing cash amount",
        variant: "destructive"
      });
      return;
    }

    let endedSession = null;
    try {
      endedSession = await sessionAPI.end(cashValue);
    } catch (error) {
      toast({
        title: "Warning",
        description: "Could not close session on server. Proceeding with logout.",
        variant: "destructive"
      });
    }

    // Use backend-calculated values from the ended session
    // Note: Use ?? instead of || to handle 0 values correctly
    const finalCashSales = endedSession?.total_cash_sales ?? sessionCashSales;
    const finalCashout = endedSession?.total_cashout ?? sessionCashout;
    const finalExpected = endedSession?.expected_cash ?? expectedCash;
    const finalDifference = endedSession?.difference ?? (cashValue - finalExpected);
    
    setHandoverData({
      userName: userName,
      userRole: userRole,
      sessionStart: localStorage.getItem('sessionStartTime') || new Date().toISOString(),
      sessionEnd: new Date().toISOString(),
      openingCash: sessionOpeningCash,
      closingCash: cashValue,
      totalCashSales: finalCashSales,
      totalSales: getTodaySales(),
      cashSales: getSalesBreakdown().cashSales,
      upiSales: getSalesBreakdown().upiSales,
      cardSales: getSalesBreakdown().cardSales,
      totalCashout: finalCashout,
      expectedCash: finalExpected,
      systemCash: finalExpected,
      invoiceCount: todayInvoices.length,
      discrepancy: finalDifference,
    });

    setShowClosingCashDialog(false);
    setShowHandoverDialog(true);
  };

  const handleConfirmLogout = () => {
    localStorage.clear();
    toast({
      title: "Session Ended",
      description: `Closing cash: ₹${handoverData?.closingCash?.toFixed(2)}`
    });
    navigate('/');
  };

  const printHandover = () => {
    if (!handoverData) return;
    const d = handoverData;
    const startTime = new Date(d.sessionStart).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
    const endTime = new Date(d.sessionEnd).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
    
    const printWindow = window.open('', '', 'width=320,height=500');
    if (!printWindow) return;
    
    const content = `
      <!DOCTYPE html><html><head><title>Shift Handover</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        @page { size: 80mm auto; margin: 0; }
        body { width:80mm; font-family:'Courier New',monospace; font-size:12px; padding:3mm; }
        .center { text-align:center; }
        .line { border-top:1px dashed #000; margin:4px 0; }
        .row { display:flex; justify-content:space-between; padding:2px 0; }
        .bold { font-weight:bold; }
        .big { font-size:14px; }
        h3 { font-size:14px; text-align:center; margin:6px 0; }
      </style></head><body>
        <h3>SHIFT HANDOVER</h3>
        <div class="line"></div>
        <div class="row"><span>User:</span><span class="bold">${d.userName}</span></div>
        <div class="row"><span>Role:</span><span>${d.userRole === 'admin' ? 'Administrator' : 'Staff'}</span></div>
        <div class="row"><span>Shift Start:</span><span>${startTime}</span></div>
        <div class="row"><span>Shift End:</span><span>${endTime}</span></div>
        <div class="line"></div>
        <div class="row"><span>Opening Cash:</span><span>Rs.${d.openingCash.toFixed(2)}</span></div>
        <div class="line"></div>
        <div class="row bold"><span>Total Sales:</span><span>Rs.${d.totalSales.toFixed(2)}</span></div>
        <div class="row" style="padding-left:8px"><span>Cash Sales:</span><span>Rs.${d.cashSales.toFixed(2)}</span></div>
        <div class="row" style="padding-left:8px"><span>UPI Sales:</span><span>Rs.${d.upiSales.toFixed(2)}</span></div>
        <div class="row" style="padding-left:8px"><span>Card Sales:</span><span>Rs.${d.cardSales.toFixed(2)}</span></div>
        <div class="row"><span>Invoices:</span><span>${d.invoiceCount}</span></div>
        <div class="line"></div>
        <div class="row"><span>Cashout:</span><span>-Rs.${d.totalCashout.toFixed(2)}</span></div>
        <div class="line"></div>
        <div class="row bold big"><span>System Cash:</span><span>Rs.${d.systemCash.toFixed(2)}</span></div>
        <div class="row bold big"><span>Closing Cash:</span><span>Rs.${d.closingCash.toFixed(2)}</span></div>
        <div class="line"></div>
        <div class="row bold" style="color:${d.discrepancy >= 0 ? 'green' : 'red'}">
          <span>${d.discrepancy >= 0 ? 'Excess:' : 'Short:'}</span>
          <span>Rs.${Math.abs(d.discrepancy).toFixed(2)}</span>
        </div>
        <div class="line"></div>
        <div class="center" style="margin-top:8px;font-size:10px">Generated: ${new Date().toLocaleString('en-IN')}</div>
      </body></html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => { printWindow.print(); setTimeout(() => printWindow.close(), 1000); }, 300);
    };
  };

  const handleClosingCashChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setClosingCash(value);
    }
  };

  // User Management Functions
  const handleAddUser = async () => {
    if (!newUser.username || !newUser.password || !newUser.name) {
      toast({
        title: "Error",
        description: "Please fill all fields",
        variant: "destructive"
      });
      return;
    }

    try {
      const createdUser = await userAPI.create(newUser);
      setUsers([...users, createdUser]);
      setShowAddUserDialog(false);
      setNewUser({ username: '', password: '', name: '', role: 'user' });
      
      toast({
        title: "Success",
        description: "User created successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create user",
        variant: "destructive"
      });
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === localStorage.getItem('userId')) {
      toast({
        title: "Error",
        description: "Cannot delete your own account",
        variant: "destructive"
      });
      return;
    }

    try {
      await userAPI.delete(userId);
      setUsers(users.filter(u => u.id !== userId));
      
      toast({
        title: "Success",
        description: "User deleted successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete user",
        variant: "destructive"
      });
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser({ ...user, password: '' });
    setShowEditUserDialog(true);
  };

  const handleSaveUserPassword = async () => {
    if (!selectedUser || !selectedUser.password) {
      toast({
        title: "Error",
        description: "Please enter a new password",
        variant: "destructive"
      });
      return;
    }

    try {
      await userAPI.update(selectedUser.id, { password: selectedUser.password });
      setShowEditUserDialog(false);
      setSelectedUser(null);
      
      toast({
        title: "Success",
        description: "Password reset successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to reset password",
        variant: "destructive"
      });
    }
  };

  // Change Own Password
  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Error",
        description: "New passwords do not match",
        variant: "destructive"
      });
      return;
    }

    if (passwordData.newPassword.length < 4) {
      toast({
        title: "Error",
        description: "Password must be at least 4 characters",
        variant: "destructive"
      });
      return;
    }

    try {
      const userId = localStorage.getItem('userId');
      await userAPI.update(userId, { password: passwordData.newPassword });
      setShowChangePasswordDialog(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
      toast({
        title: "Success",
        description: "Password changed successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to change password",
        variant: "destructive"
      });
    }
  };

  // Edit Profile
  const handleEditProfile = async () => {
    if (!profileData.name.trim()) {
      toast({
        title: "Error",
        description: "Name cannot be empty",
        variant: "destructive"
      });
      return;
    }

    try {
      const userId = localStorage.getItem('userId');
      await userAPI.update(userId, { name: profileData.name });
      localStorage.setItem('userName', profileData.name);
      setShowEditProfileDialog(false);
      
      toast({
        title: "Success",
        description: "Profile updated successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update profile",
        variant: "destructive"
      });
    }
  };

  // Vendor Management Functions
  const handleAddVendor = async () => {
    if (!newVendor.name || !newVendor.contact) {
      toast({
        title: "Error",
        description: "Please fill vendor name and contact",
        variant: "destructive"
      });
      return;
    }

    try {
      const createdVendor = await vendorAPI.create(newVendor);
      setVendors([...vendors, createdVendor]);
      setShowAddVendorDialog(false);
      setNewVendor({ name: '', contact: '', address: '', gst: '', email: '' });
      
      toast({
        title: "Success",
        description: "Vendor added successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to add vendor",
        variant: "destructive"
      });
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    try {
      await vendorAPI.delete(vendorId);
      setVendors(vendors.filter(v => v.id !== vendorId));
      
      toast({
        title: "Success",
        description: "Vendor deleted successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete vendor",
        variant: "destructive"
      });
    }
  };

  const getTodaySales = () => salesStats.total_sales || 0;

  // Calculate sales breakdown from invoices with split payment support
  const calculateSalesBreakdown = (invoices) => {
    let cashSales = 0;
    let upiSales = 0;
    let cardSales = 0;
    let onlineSales = 0;
    let totalSales = 0;

    invoices.forEach(inv => {
      const amount = inv.total || inv.total_amount || 0;
      totalSales += amount;
      
      if (inv.payment_method === 'Split' && inv.payment_details) {
        // Break down split payments into respective modes
        cashSales += parseFloat(inv.payment_details.cash) || 0;
        upiSales += parseFloat(inv.payment_details.upi) || 0;
        cardSales += parseFloat(inv.payment_details.card) || 0;
      } else if (inv.payment_method === 'Cash') {
        cashSales += amount;
      } else if (inv.payment_method === 'UPI') {
        upiSales += amount;
      } else if (inv.payment_method === 'Card') {
        cardSales += amount;
      } else if (inv.payment_method && inv.payment_method.startsWith('Online')) {
        onlineSales += amount;
      }
    });

    return { cashSales, upiSales, cardSales, onlineSales, totalSales };
  };

  // Today's sales breakdown (with split payment support)
  const getTodaySalesBreakdown = () => calculateSalesBreakdown(todayInvoices);
  
  // Yesterday's sales breakdown (with split payment support)
  const getYesterdaySalesBreakdown = () => calculateSalesBreakdown(yesterdayInvoices);

  // Legacy getSalesBreakdown for compatibility
  const getSalesBreakdown = () => ({
    upiSales: salesStats.upi_sales || 0,
    cardSales: salesStats.card_sales || 0,
    cashSales: salesStats.cash_sales || 0
  });

  const getTodayCashout = () => salesStats.total_cashout || 0;

  const getSystemCash = () => {
    // Use backend-calculated expected cash (Opening + Cash Sales - Cashouts)
    return expectedCash;
  };

  const handlePrintInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDialog(true);
  };

  const printBill = () => {
    if (!selectedInvoice) return;
    
    const printWindow = window.open('', '', 'width=320,height=600');
    
    if (!printWindow) {
      toast({
        title: "Print Blocked",
        description: "Please allow popups to print",
        variant: "destructive"
      });
      return;
    }
    
    const pad = (str, len, align = 'left') => {
      str = String(str);
      if (str.length > len) str = str.substring(0, len);
      if (align === 'right') return str.padStart(len);
      if (align === 'center') {
        const padLen = len - str.length;
        const padLeft = Math.floor(padLen / 2);
        return str.padStart(str.length + padLeft).padEnd(len);
      }
      return str.padEnd(len);
    };
    
    const center = (str, width = 42) => pad(str, width, 'center');
    const line = '='.repeat(42);
    const dashLine = '-'.repeat(42);
    
    let receipt = '';
    receipt += center('PLEASE VISIT AGAIN') + '\n';
    receipt += center('╔═══════════╗') + '\n';
    receipt += center('║  NEW SHOP ║') + '\n';
    receipt += center('╚═══════════╝') + '\n';
    receipt += line + '\n';
    receipt += center('SALES INVOICE') + '\n';
    receipt += line + '\n';
    receipt += `Invoice: ${selectedInvoice.invoice_no}\n`;
    receipt += `Date: ${selectedInvoice.date}    Time: ${selectedInvoice.time}\n`;
    receipt += dashLine + '\n';
    receipt += 'Ground floor, Shop no. 5 & 6, Ostwal\n';
    receipt += 'Ashadeep, Near Silver Park Signal,\n';
    receipt += 'Mira Bhayandar Rd, Thane, MH-401107\n';
    receipt += 'Ph: 7718936910 / 8976352694\n';
    receipt += 'GSTIN: 27BSTPM1629P1Z\n';
    receipt += 'FSSAI: 1152502000022\n';
    receipt += line + '\n';
    
    receipt += pad('Item', 20) + pad('Qty', 5, 'right') + pad('Rate', 8, 'right') + pad('Amt', 9, 'right') + '\n';
    receipt += dashLine + '\n';
    
    selectedInvoice.items.forEach(item => {
      const name = item.name.length > 20 ? item.name.substring(0, 19) + '.' : item.name;
      const price = item.sales_price || item.price;
      receipt += pad(name, 20) + pad(item.quantity, 5, 'right') + pad(price, 8, 'right') + pad(item.item_total.toFixed(0), 9, 'right') + '\n';
    });
    
    receipt += line + '\n';
    receipt += pad('Total Qty:', 25) + pad(selectedInvoice.total_quantity, 17, 'right') + '\n';
    receipt += pad('Subtotal:', 25) + pad('Rs.' + selectedInvoice.subtotal.toFixed(2), 17, 'right') + '\n';
    receipt += pad('CGST:', 25) + pad('Rs.' + selectedInvoice.cgst.toFixed(2), 17, 'right') + '\n';
    receipt += pad('SGST:', 25) + pad('Rs.' + selectedInvoice.sgst.toFixed(2), 17, 'right') + '\n';
    receipt += pad('Savings:', 25) + pad('Rs.' + selectedInvoice.savings.toFixed(2), 17, 'right') + '\n';
    receipt += dashLine + '\n';
    receipt += pad('TOTAL:', 25) + pad('Rs.' + selectedInvoice.total.toFixed(2), 17, 'right') + '\n';
    receipt += line + '\n';
    receipt += center('Payment: ' + selectedInvoice.payment_method) + '\n';
    receipt += line + '\n';
    receipt += center('THANK YOU FOR SHOPPING!') + '\n';
    receipt += center('No Return - No Exchange After Sale') + '\n';
    receipt += '\n\n\n';
    
    const billHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: 80mm auto; margin: 0mm; }
          @media print {
            html, body { width: 80mm; margin: 0; padding: 0; }
          }
          html, body { width: 80mm; margin: 0; padding: 0; background: #fff; color: #000; }
          pre {
            font-family: 'Courier New', Courier, monospace;
            font-size: 12px; font-weight: 500; line-height: 1.25;
            white-space: pre; margin: 0; padding: 2mm; color: #000; background: #fff;
          }
        </style>
      </head>
      <body><pre>${receipt}</pre></body>
      </html>
    `;
    
    printWindow.document.write(billHTML);
    printWindow.document.close();
    
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
          setShowInvoiceDialog(false);
        }, 1000);
      }, 300);
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#e8ecf0' }}>
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#e8ecf0' }}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_6a116685-1875-4e0c-97f5-a9213e7bf53a/artifacts/isz5lodv_IMG_6110.PNG" 
              alt="New Shop Logo" 
              className="h-10 object-contain"
              data-testid="dashboard-logo"
            />
            <span className="text-xl font-semibold text-gray-800">POS System</span>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="flex items-center space-x-2">
                <span className="text-gray-600">Welcome, <span className="font-semibold">{userName}</span></span>
                {userRole === 'admin' && (
                  <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full font-medium">Admin</span>
                )}
              </div>
              {userRole !== 'admin' && (
                <p className="text-xs text-gray-500">Opening Cash: ₹{openingCash}</p>
              )}
            </div>
            <Button
              onClick={() => setShowChangePasswordDialog(true)}
              variant="outline"
              size="sm"
              className="flex items-center space-x-1"
              data-testid="change-password-btn"
            >
              <Key className="w-4 h-4" />
              <span className="hidden md:inline">Password</span>
            </Button>
            {userRole === 'admin' && (
              <Button
                onClick={() => {
                  setProfileData({ name: userName });
                  setShowEditProfileDialog(true);
                }}
                variant="outline"
                size="sm"
                className="flex items-center space-x-1"
                data-testid="edit-profile-btn"
              >
                <Edit className="w-4 h-4" />
                <span className="hidden md:inline">Profile</span>
              </Button>
            )}
            <Button
              onClick={handleLogoutClick}
              variant="outline"
              className="flex items-center space-x-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Dashboard</h1>
        
        {/* Sales Stats Grid with Today/Yesterday selector */}
        {(() => {
          const isToday = salesView === 'today';
          const b = isToday ? getTodaySalesBreakdown() : getYesterdaySalesBreakdown();
          const count = isToday ? todayInvoices.length : yesterdayInvoices.length;
          const stats = [
            { key: 'total', label: 'Total Sales', icon: Calendar, iconCls: 'text-green-600', value: `₹${b.totalSales.toFixed(2)}`, valCls: 'text-gray-900', sub: `${count} invoices` },
            { key: 'cash', label: 'Cash', icon: DollarSign, iconCls: 'text-blue-600', value: `₹${b.cashSales.toFixed(2)}`, valCls: 'text-green-600' },
            { key: 'upi', label: 'UPI', icon: TrendingUp, iconCls: 'text-purple-600', value: `₹${b.upiSales.toFixed(2)}`, valCls: 'text-purple-600' },
            { key: 'card', label: 'Card', icon: FileText, iconCls: 'text-blue-500', value: `₹${b.cardSales.toFixed(2)}`, valCls: 'text-blue-500' },
            { key: 'online', label: 'Online', icon: ShoppingCart, iconCls: 'text-orange-500', value: `₹${b.onlineSales.toFixed(2)}`, valCls: 'text-orange-500' },
            { key: 'invoices', label: 'Invoices', icon: FileText, iconCls: 'text-gray-600', value: count, valCls: 'text-gray-900' },
          ];
          return (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-700" data-testid="sales-view-heading">{isToday ? "Today's Sales" : "Yesterday's Sales"}</h2>
                <Select value={salesView} onValueChange={setSalesView}>
                  <SelectTrigger className="w-40 bg-white" data-testid="sales-view-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today" data-testid="sales-view-today">Today</SelectItem>
                    <SelectItem value="yesterday" data-testid="sales-view-yesterday">Yesterday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                {stats.map(({ key, label, icon: Icon, iconCls, value, valCls, sub }) => (
                  <Card key={key} className="bg-white hover:shadow-lg transition-shadow">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-xs font-medium text-gray-600">{label}</CardTitle>
                      <Icon className={`w-4 h-4 ${iconCls}`} />
                    </CardHeader>
                    <CardContent>
                      <div className={`text-xl font-bold ${valCls}`} data-testid={`sales-stat-${key}`}>{value}</div>
                      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          );
        })()}

        {/* Quick Actions */}
        <Card className="bg-white mb-8">
          <CardHeader>
            <CardTitle className="text-xl">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            {userRole === 'admin' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <Button className="h-24 bg-gray-800 hover:bg-gray-900 text-white font-semibold text-lg" onClick={() => navigate('/sales?action=addProduct')} data-testid="add-product-btn">
                  <Package className="w-6 h-6 mr-2" />
                  Add Product
                </Button>
                <Button className="h-24 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg" onClick={() => navigate('/reports')} data-testid="reports-btn">
                  <FileText className="w-6 h-6 mr-2" />
                  Reports
                </Button>
                <Button className="h-24 bg-orange-600 hover:bg-orange-700 text-white font-semibold text-lg" onClick={() => setShowAddVendorDialog(true)} data-testid="add-vendor-btn">
                  <Truck className="w-6 h-6 mr-2" />
                  Add Vendor
                </Button>
                <Button className="h-24 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-lg" onClick={() => setShowUserManagement(true)} data-testid="user-management-btn">
                  <Users className="w-6 h-6 mr-2" />
                  Users
                </Button>
                <Button className="h-24 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-lg" onClick={() => setShowVendorList(true)} data-testid="view-vendors-btn">
                  <Truck className="w-6 h-6 mr-2" />
                  View Vendors
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <Button className="h-24 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold text-lg" onClick={() => navigate('/sales')} data-testid="new-sale-btn">
                  <ShoppingCart className="w-6 h-6 mr-2" />
                  New Sale
                </Button>
                <Button className="h-24 bg-gray-800 hover:bg-gray-900 text-white font-semibold text-lg" onClick={() => navigate('/sales?action=addProduct')} data-testid="add-product-btn">
                  <Package className="w-6 h-6 mr-2" />
                  Add Product
                </Button>
                <Button className="h-24 bg-purple-600 hover:bg-purple-700 text-white font-semibold text-lg" onClick={() => navigate('/rtv')} data-testid="rtv-btn">
                  <Package className="w-6 h-6 mr-2" />
                  RTV
                </Button>
                <Button className="h-24 bg-green-600 hover:bg-green-700 text-white font-semibold text-lg" onClick={() => navigate('/cashout')} data-testid="cashout-btn">
                  <TrendingUp className="w-6 h-6 mr-2" />
                  Cashout
                </Button>
                <Button className="h-24 bg-orange-600 hover:bg-orange-700 text-white font-semibold text-lg" onClick={() => setShowAddVendorDialog(true)} data-testid="add-vendor-btn-user">
                  <Truck className="w-6 h-6 mr-2" />
                  Add Vendor
                </Button>
                <Button className="h-24 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-lg" onClick={() => setShowVendorList(true)} data-testid="view-vendors-btn-user">
                  <Truck className="w-6 h-6 mr-2" />
                  View Vendors
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Today's and Yesterday's Invoices */}
        <Card className="bg-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">Invoices</CardTitle>
              <div className="flex space-x-2">
                <Button
                  onClick={() => setActiveInvoiceTab('today')}
                  variant={activeInvoiceTab === 'today' ? 'default' : 'outline'}
                  size="sm"
                  className={activeInvoiceTab === 'today' ? 'bg-yellow-400 hover:bg-yellow-500 text-black' : ''}
                >
                  Today ({todayInvoices.length})
                </Button>
                <Button
                  onClick={() => setActiveInvoiceTab('yesterday')}
                  variant={activeInvoiceTab === 'yesterday' ? 'default' : 'outline'}
                  size="sm"
                  className={activeInvoiceTab === 'yesterday' ? 'bg-yellow-400 hover:bg-yellow-500 text-black' : ''}
                >
                  Yesterday ({yesterdayInvoices.length})
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {activeInvoiceTab === 'today' ? (
              todayInvoices.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No invoices generated today</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {todayInvoices.map((invoice) => (
                    <div key={invoice.invoice_no} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div>
                        <p className="font-semibold text-gray-900">{invoice.invoice_no}</p>
                        <p className="text-sm text-gray-500">{invoice.time} • {invoice.items.length} items</p>
                        {invoice.created_by_username && (
                          <p className="text-xs text-gray-400">by {invoice.created_by_username}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="font-bold text-gray-900">₹{invoice.total.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{invoice.payment_method}</p>
                        </div>
                        <Button
                          onClick={() => handlePrintInvoice(invoice)}
                          variant="outline"
                          size="sm"
                          className="flex items-center space-x-1"
                        >
                          <Printer className="w-4 h-4" />
                          <span>Reprint</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              yesterdayInvoices.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No invoices from yesterday</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {yesterdayInvoices.map((invoice) => (
                    <div key={invoice.invoice_no} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div>
                        <p className="font-semibold text-gray-900">{invoice.invoice_no}</p>
                        <p className="text-sm text-gray-500">{invoice.date} {invoice.time} • {invoice.items.length} items</p>
                        {invoice.created_by_username && (
                          <p className="text-xs text-gray-400">by {invoice.created_by_username}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="font-bold text-gray-900">₹{invoice.total.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{invoice.payment_method}</p>
                        </div>
                        <Button
                          onClick={() => handlePrintInvoice(invoice)}
                          variant="outline"
                          size="sm"
                          className="flex items-center space-x-1"
                        >
                          <Printer className="w-4 h-4" />
                          <span>Reprint</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice Print Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="sm:max-w-md p-0 bg-white">
          <div className="w-full bg-white overflow-auto" style={{ maxHeight: '500px' }}>
            {selectedInvoice && (
              <pre className="p-3 text-black bg-white" style={{ fontFamily: "'Courier New', Courier, monospace", fontSize: '11px', lineHeight: '1.2', whiteSpace: 'pre', margin: 0 }}>
{`       PLEASE VISIT AGAIN
         ╔═══════════╗
         ║  NEW SHOP ║
         ╚═══════════╝
==========================================
          SALES INVOICE
==========================================
Invoice: ${selectedInvoice.invoice_no}
Date: ${selectedInvoice.date}    Time: ${selectedInvoice.time}
------------------------------------------
Ground floor, Shop no. 5 & 6, Ostwal
Ashadeep, Near Silver Park Signal,
Mira Bhayandar Rd, Thane, MH-401107
Ph: 7718936910 / 8976352694
GSTIN: 27BSTPM1629P1Z
FSSAI: 1152502000022
==========================================
Item                 Qty    Rate      Amt
------------------------------------------
${selectedInvoice.items.map(item => {
  const name = item.name.length > 20 ? item.name.substring(0, 19) + '.' : item.name.padEnd(20);
  const qty = String(item.quantity).padStart(3);
  const price = String(item.sales_price || item.price).padStart(7);
  const amt = item.item_total.toFixed(0).padStart(8);
  return `${name}${qty}${price}${amt}`;
}).join('\n')}
==========================================
Total Qty:                ${String(selectedInvoice.total_quantity).padStart(17)}
Subtotal:          Rs.${selectedInvoice.subtotal.toFixed(2).padStart(14)}
CGST:              Rs.${selectedInvoice.cgst.toFixed(2).padStart(14)}
SGST:              Rs.${selectedInvoice.sgst.toFixed(2).padStart(14)}
Savings:           Rs.${selectedInvoice.savings.toFixed(2).padStart(14)}
------------------------------------------
TOTAL:             Rs.${selectedInvoice.total.toFixed(2).padStart(14)}
==========================================
      Payment: ${selectedInvoice.payment_method}
==========================================
    THANK YOU FOR SHOPPING!
  No Return - No Exchange After Sale`}
              </pre>
            )}
          </div>
          <DialogFooter className="p-4 border-t">
            <div className="flex space-x-2 w-full">
              <Button onClick={printBill} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold">
                Print Bill
              </Button>
              <Button onClick={() => setShowInvoiceDialog(false)} variant="outline" className="flex-1">
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Closing Cash Dialog */}
      <Dialog open={showClosingCashDialog} onOpenChange={(open) => { if (!open) handleCancelLogout(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center space-x-2">
              <DollarSign className="w-6 h-6 text-red-600" />
              <span>Closing Cash</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Please enter the closing cash amount to end your session.
            </p>
            
            {/* Shift Cash Reconciliation */}
            <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-2">
              <h4 className="font-semibold text-gray-700 border-b pb-2 mb-2">Shift Cash Reconciliation</h4>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Opening Cash:</span>
                <span className="font-bold text-gray-800">₹{sessionOpeningCash.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm font-medium text-blue-700">Cash Sales (this shift):</span>
                <span className="font-bold text-blue-700">+₹{sessionCashSales.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm text-gray-600">Cashout (this shift):</span>
                <span className="font-medium text-orange-600">-₹{sessionCashout.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between items-center pt-2 border-t bg-yellow-50 -mx-4 px-4 py-2 rounded-b">
                <span className="text-sm font-semibold text-gray-700">Expected Cash in Drawer:</span>
                <span className="font-bold text-lg text-green-700">₹{expectedCash.toFixed(2)}</span>
              </div>
            </div>
            
            {/* Closing Cash Input */}
            <div>
              <Label htmlFor="closingCash" className="text-sm font-medium">Closing Cash (₹)</Label>
              <div className="relative mt-2">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                <Input
                  id="closingCash"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={closingCash}
                  onChange={handleClosingCashChange}
                  className="pl-8 text-lg font-semibold"
                  autoFocus
                  data-testid="closing-cash-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Enter amount with up to 2 decimal places</p>
              
              {closingCash && Math.abs(parseFloat(closingCash) - getSystemCash()) > 0.01 && (
                <div className={`mt-2 p-2 rounded text-xs ${parseFloat(closingCash) > getSystemCash() ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {parseFloat(closingCash) > getSystemCash() 
                    ? `Excess: +₹${(parseFloat(closingCash) - getSystemCash()).toFixed(2)}`
                    : `Short: -₹${(getSystemCash() - parseFloat(closingCash)).toFixed(2)}`
                  }
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <div className="flex space-x-2 w-full">
              <Button
                onClick={handleClosingCashSubmit}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
                data-testid="closing-cash-submit"
              >
                End Session & Logout
              </Button>
              <Button
                onClick={handleCancelLogout}
                variant="outline"
                className="flex-1"
                data-testid="closing-cash-cancel"
              >
                Cancel
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Handover Dialog */}
      <Dialog open={showHandoverDialog} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-lg" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center space-x-2">
              <FileText className="w-6 h-6 text-blue-600" />
              <span>Shift Handover Summary</span>
            </DialogTitle>
          </DialogHeader>
          {handoverData && (
            <div className="py-2">
              {/* User & Time */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">User</span>
                  <span className="font-semibold">{handoverData.userName} ({handoverData.userRole === 'admin' ? 'Admin' : 'Staff'})</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Shift Start</span>
                  <span>{new Date(handoverData.sessionStart).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Shift End</span>
                  <span>{new Date(handoverData.sessionEnd).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</span>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="space-y-2 mb-3">
                <div className="flex justify-between items-center py-1.5 border-b">
                  <span className="text-sm text-gray-600">Opening Cash</span>
                  <span className="font-semibold">₹{handoverData.openingCash.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b">
                  <span className="text-sm font-medium text-blue-700">Cash Sales (this shift)</span>
                  <span className="font-bold text-blue-700">+₹{(handoverData.totalCashSales ?? handoverData.cashSales).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b">
                  <span className="text-sm text-gray-600">Cashout</span>
                  <span className="font-medium text-orange-600">-₹{handoverData.totalCashout.toFixed(2)}</span>
                </div>
              </div>

              {/* Cash Reconciliation */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-700">Expected Cash in Drawer</span>
                  <span className="font-bold text-lg">₹{(handoverData.expectedCash ?? handoverData.systemCash).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-700">Closing Cash (Entered)</span>
                  <span className="font-bold text-lg">₹{handoverData.closingCash.toFixed(2)}</span>
                </div>
                <div className={`flex justify-between items-center pt-2 border-t ${handoverData.discrepancy >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  <span className="text-sm font-semibold">{handoverData.discrepancy >= 0 ? 'Excess' : 'Short'}</span>
                  <span className="font-bold text-lg">{handoverData.discrepancy >= 0 ? '+' : '-'}₹{Math.abs(handoverData.discrepancy).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <div className="flex space-x-2 w-full">
              <Button
                onClick={printHandover}
                variant="outline"
                className="flex-1"
                data-testid="handover-print"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button
                onClick={handleConfirmLogout}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
                data-testid="handover-confirm-logout"
              >
                Confirm Logout
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Management Dialog */}
      <Dialog open={showUserManagement} onOpenChange={setShowUserManagement}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center space-x-2">
              <Users className="w-6 h-6" />
              <span>User Management</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">Manage user accounts</p>
              <Button onClick={() => setShowAddUserDialog(true)} className="bg-green-600 hover:bg-green-700" data-testid="add-user-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {users.map(user => (
                <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.role === 'admin' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                      {user.role === 'admin' ? <Shield className="w-5 h-5 text-purple-600" /> : <Users className="w-5 h-5 text-blue-600" />}
                    </div>
                    <div>
                      <p className="font-semibold">{user.name}</p>
                      <p className="text-sm text-gray-500">@{user.username} • {user.role === 'admin' ? 'Administrator' : 'Staff User'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button onClick={() => handleResetPassword(user)} variant="outline" size="sm">
                      <Key className="w-4 h-4 mr-1" />
                      Reset Password
                    </Button>
                    <Button 
                      onClick={() => handleDeleteUser(user.id)} 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={user.id === localStorage.getItem('userId')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Add New User</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                placeholder="Enter full name"
              />
            </div>
            <div>
              <Label>Username</Label>
              <Input
                value={newUser.username}
                onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                placeholder="Enter username"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                placeholder="Enter password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Staff User</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleAddUser} className="w-full bg-green-600 hover:bg-green-700">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset User Password Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Reset Password</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-gray-600">Reset password for: <span className="font-semibold">{selectedUser?.name}</span></p>
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={selectedUser?.password || ''}
                onChange={(e) => setSelectedUser({...selectedUser, password: e.target.value})}
                placeholder="Enter new password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSaveUserPassword} className="w-full bg-blue-600 hover:bg-blue-700">
              Save Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Own Password Dialog */}
      <Dialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center space-x-2">
              <Key className="w-5 h-5" />
              <span>Change Password</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                placeholder="Enter new password"
              />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                placeholder="Confirm new password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleChangePassword} className="w-full bg-blue-600 hover:bg-blue-700">
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfileDialog} onOpenChange={setShowEditProfileDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center space-x-2">
              <Edit className="w-5 h-5" />
              <span>Edit Profile</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Display Name</Label>
              <Input
                value={profileData.name}
                onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                placeholder="Enter display name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleEditProfile} className="w-full bg-blue-600 hover:bg-blue-700">
              Save Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Vendor Dialog */}
      <Dialog open={showAddVendorDialog} onOpenChange={setShowAddVendorDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center space-x-2">
              <Truck className="w-5 h-5" />
              <span>Add New Vendor</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Vendor Name *</Label>
              <Input
                value={newVendor.name}
                onChange={(e) => setNewVendor({...newVendor, name: e.target.value})}
                placeholder="Enter vendor name"
              />
            </div>
            <div>
              <Label>Contact Number *</Label>
              <Input
                value={newVendor.contact}
                onChange={(e) => setNewVendor({...newVendor, contact: e.target.value})}
                placeholder="Enter contact number"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newVendor.email}
                onChange={(e) => setNewVendor({...newVendor, email: e.target.value})}
                placeholder="Enter email address"
              />
            </div>
            <div>
              <Label>GST Number</Label>
              <Input
                value={newVendor.gst}
                onChange={(e) => setNewVendor({...newVendor, gst: e.target.value})}
                placeholder="Enter GST number"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={newVendor.address}
                onChange={(e) => setNewVendor({...newVendor, address: e.target.value})}
                placeholder="Enter address"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleAddVendor} className="w-full bg-orange-600 hover:bg-orange-700">
              Add Vendor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Vendors Dialog */}
      <Dialog open={showVendorList} onOpenChange={setShowVendorList}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center space-x-2">
              <Truck className="w-6 h-6" />
              <span>Vendor List</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {vendors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No vendors added yet</p>
                <Button onClick={() => { setShowVendorList(false); setShowAddVendorDialog(true); }} className="mt-4 bg-orange-600 hover:bg-orange-700">
                  Add Your First Vendor
                </Button>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {vendors.map(vendor => (
                  <div key={vendor.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <Truck className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-semibold">{vendor.name}</p>
                        <p className="text-sm text-gray-500">{vendor.contact} {vendor.email && `• ${vendor.email}`}</p>
                        {vendor.gst && <p className="text-xs text-gray-400">GST: {vendor.gst}</p>}
                      </div>
                    </div>
                    {userRole === 'admin' && (
                      <Button 
                        onClick={() => handleDeleteVendor(vendor.id)} 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-vendor-${vendor.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
